/* eslint-disable max-classes-per-file */
// @flow

import type { MonetaryAmountRange } from 'modules/monetary-amount';

export const externalPricesKey = (
  orderType: string | null,
  catalogKey: string,
  customerNumber: string,
): string => `${orderType || ''}_${catalogKey}_${customerNumber}`;

/* 🤔 Is this making any sense? */
export type ExternalPricesQuery = {
  catalogKey: string,
  customerNumber: string,
  orderType: string,
  variationIds: string[],
  productIds: string[],
  stockItemIds: string[],
};

export type SerializedExternalPricesQuery = {
  catalog_key: string,
  customer_number: string,
  variation_ids: string,
  order_type: string,
};

export type ExternalPriceSettings = $ReadOnly<{|
  externalPriceTotalsShouldFallBackToWholesalePrice: boolean,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
|}>;

class StockItemExternalPriceError extends Error {
  // standard error
}

export class FailedToFindExternalPrices extends Error {
  title: 'failed_to_find_prices';

  constructor() {
    super('FailedToFindExternalPrices');
    // eslint-disable-next-line fp/no-mutation
    this.title = 'failed_to_find_prices';
  }
}

export class ExternalPriceProviderError extends Error {
  title: 'external_price_provider_error';

  messages: $ReadOnlyArray<StockItemExternalPriceError>;

  constructor(messages: $ReadOnlyArray<string>) {
    super('StockItemExternalPriceError');
    // eslint-disable-next-line fp/no-mutation
    this.title = 'external_price_provider_error';
    // eslint-disable-next-line fp/no-mutation
    this.messages = messages.map(
      (message) => new StockItemExternalPriceError(message),
    );
  }
}

/* 💁 This is specific to quiksilver.

They never gave us explicit instructions on how to parse their SAP
errors. This is what we deduced.

Here's an example error:
{
    FIELD: '',
    ID: 'ZBGX_MC_PROD_SEARCH',
    LOG_MSG_NO: '000000',
    LOG_NO: '',
    MESSAGE: 'Error in getting material grid',
    MESSAGE_V1: 'ERGW103013-XKKM',
    MESSAGE_V2: '',
    MESSAGE_V3: '',
    MESSAGE_V4: '',
    NUMBER: '017',
    PARAMETER: '',
    ROW: '0 ',
    SYSTEM: 'QSQCLNT100',
    TYPE: 'W',
  }
*/
export const parsedErrors = (
  errors: Array<{
    FIELD: string,
    ID: string,
    LOG_MSG_NO: string,
    LOG_NO: string,
    MESSAGE: string,
    MESSAGE_V1: string,
    MESSAGE_V2: string,
    MESSAGE_V3: string,
    MESSAGE_V4: string,
    NUMBER: string,
    PARAMETER: string,
    ROW: string,
    SYSTEM: string,
    TYPE: string,
  }>,
): {
  productNumber?: string,
  variationCode?: string,
  clientMessage: string,
}[] =>
  errors.map(({ MESSAGE_V1, MESSAGE }) => {
    const result = /([^-]*)-(.*)/.exec(MESSAGE_V1);
    // eslint-disable-next-line no-unused-vars
    const [_match, productNumber, variationCode] = result || [];

    return { productNumber, variationCode, clientMessage: MESSAGE };
  });

export const minMaxRange = (
  amounts: $ReadOnlyArray<number>,
): MonetaryAmountRange =>
  amounts.length !== 0
    ? {
        low: Math.min(...amounts),
        high: Math.max(...amounts),
      }
    : {
        low: 0,
        high: 0,
      };
